import React, { useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import CalendarDayPopup from './CalendarDayPopup'
import PaceDot from './PaceDot'
import { isWeekend } from '@/lib/calendar-utils'
import { formatPickupRooms } from '@/lib/plain-language'

interface DayInfo {
  date: string
  dayOfWeek: string
  dayOfMonth: number
}

interface OccupancyDay {
  date: string
  booked: number
  available: number
  occupancyPct: number
  pickup7d: number | null
  pickup30d?: number | null
  pickup60d?: number | null
  pickup7dRooms?: number | null
  pickup30dRooms?: number | null
  pickup60dRooms?: number | null
  paceRatio?: number | null
  baselineOccPct?: number | null
}

interface RateData {
  rateAmount: number
  source: string
  stopSell: boolean
  closedToArrival: boolean
  closedToDeparture: boolean
  minStayArrival: number | null
  maxStay: number | null
  computationDetails: Record<string, unknown>
  pushedRateAmount: number | null
  syncStatus: 'synced' | 'pending' | 'never_pushed'
}

interface OccupancyGridProps {
  daysInMonth: DayInfo[]
  occupancyData: OccupancyDay[]
  propertyOccupancyByDate?: Record<string, { booked: number; available: number; occupancyPct: number; label?: string | null }>
  currency?: string
  rateByDate?: Record<string, RateData>
  onCellClick?: (date: string) => void
  adrData?: Record<string, { adr: number | null; lastYearAdr: number | null }>
  /** Today AT THE PROPERTY (YYYY-MM-DD) — see calendar-utils#todayIso. */
  today: string
}

function occupancyColor(pct: number): string {
  if (pct >= 80) return 'text-emerald-700 bg-emerald-50/80'
  if (pct >= 60) return 'text-amber-700 bg-amber-50/80'
  return 'text-slate-600 bg-slate-50/50'
}

// Pickup is shown in ROOMS (docs/revenue-vocabulary.md § Plain-language). Rooms picked up →
// emerald, rooms lost (cancellations) → rose, no change / unknown → muted.
function pickupRoomsCell(rooms: number | null | undefined): React.ReactNode {
  return (
    <span
      className={cn(
        'font-semibold tabular-nums',
        rooms != null && rooms > 0 ? 'text-emerald-600' : rooms != null && rooms < 0 ? 'text-rose-600' : 'text-muted-foreground'
      )}
    >
      {formatPickupRooms(rooms)}
    </span>
  )
}

const METRIC_LABELS = ['Booked', 'Available', 'Occupancy %', 'Pickup (7d)', 'Pickup (30d)', 'Pickup (60d)'] as const
const METRIC_COLORS: Record<string, string> = {
  'Booked': 'text-primary/80',
  'Available': 'text-muted-foreground',
  'Occupancy %': 'text-foreground',
  'Pickup (7d)': 'text-muted-foreground',
  'Pickup (30d)': 'text-muted-foreground',
  'Pickup (60d)': 'text-muted-foreground',
}

function OccupancyGrid({ daysInMonth, occupancyData, propertyOccupancyByDate, currency, rateByDate, onCellClick, adrData, today: propertyToday }: OccupancyGridProps) {
  // Hooks must run unconditionally — keep them above any early return.
  const todayRef = useRef<HTMLTableCellElement>(null)
  useEffect(() => {
    todayRef.current?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' })
  }, [])

  if (daysInMonth.length === 0) {
    return (
      <div className="rounded-xl border border-border/60 bg-white p-10 text-center shadow-sm">
        <p className="text-sm text-muted-foreground">No occupancy data available.</p>
      </div>
    )
  }

  const dataByDate: Record<string, OccupancyDay> = {}
  for (const day of occupancyData) {
    dataByDate[day.date] = day
  }

  return (
    <TooltipProvider delayDuration={800} skipDelayDuration={0}>
      <div className="overflow-x-auto rounded-xl border border-border/60 bg-white shadow-sm">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 min-w-[140px] border-b border-r border-border/40 bg-white px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Metric
              </th>
              {daysInMonth.map((day) => {
                const weekend = isWeekend(day.dayOfWeek)
                const today = day.date === propertyToday
                const past = day.date < propertyToday
                return (
                  <th
                    key={day.date}
                    ref={today ? todayRef : undefined}
                    className={cn(
                      'min-w-[60px] border-b border-r border-border/30 px-1.5 py-2.5 text-center',
                      past && 'opacity-40',
                      today ? 'bg-primary/5' : weekend ? 'bg-slate-50' : 'bg-white'
                    )}
                  >
                    <div className={cn(
                      'text-[10px] font-medium',
                      weekend ? 'text-primary/70' : 'text-muted-foreground'
                    )}>
                      {day.dayOfWeek}
                    </div>
                    <div className={cn(
                      'text-xs font-semibold',
                      today ? 'text-primary' : 'text-foreground'
                    )}>
                      {day.dayOfMonth}
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {METRIC_LABELS.map((label, idx) => (
              <tr key={label} className={idx % 2 === 0 ? '' : 'bg-slate-50/30'}>
                <td className={cn(
                  'sticky left-0 z-10 border-b border-r border-border/40 bg-white px-4 py-2 text-[13px] font-semibold whitespace-nowrap',
                  METRIC_COLORS[label]
                )}>
                  {label}
                </td>
                {daysInMonth.map((day) => {
                  const data = dataByDate[day.date]
                  const weekend = isWeekend(day.dayOfWeek)
                  const today = day.date === propertyToday
                  const past = day.date < propertyToday

                  if (!data) {
                    return (
                      <td
                        key={day.date}
                        className={cn(
                          'border-b border-r border-border/30 px-1.5 py-2 text-center text-muted-foreground/40',
                          past && 'opacity-40',
                          today ? 'bg-primary/5' : weekend ? 'bg-slate-50/50' : ''
                        )}
                      >
                        &mdash;
                      </td>
                    )
                  }

                  let content: React.ReactNode
                  let extraClass = ''

                  switch (label) {
                    case 'Booked':
                      content = <span className="font-semibold tabular-nums">{data.booked}</span>
                      break
                    case 'Available':
                      content = <span className="tabular-nums text-muted-foreground">{data.available}</span>
                      break
                    case 'Occupancy %':
                      content = <span className="font-bold tabular-nums">{data.occupancyPct}%</span>
                      extraClass = occupancyColor(data.occupancyPct)
                      break
                    case 'Pickup (7d)':
                      content = pickupRoomsCell(data.pickup7dRooms)
                      break
                    case 'Pickup (30d)':
                      content = pickupRoomsCell(data.pickup30dRooms)
                      break
                    case 'Pickup (60d)':
                      content = pickupRoomsCell(data.pickup60dRooms)
                      break
                  }

                  return (
                    <td
                      key={day.date}
                      className={cn(
                        'relative border-b border-r border-border/30 p-0 text-[13px]',
                        past && 'opacity-40',
                        today && !extraClass && 'bg-primary/5',
                        weekend && !today && !extraClass && 'bg-slate-50/50',
                        extraClass
                      )}
                    >
                      {label === 'Booked' && (
                        <PaceDot paceRatio={data.paceRatio} occPct={data.occupancyPct} baselinePct={data.baselineOccPct} className="absolute top-0.5 right-0.5" />
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            onClick={() => onCellClick?.(day.date)}
                            className="flex items-center justify-center px-1.5 py-2 cursor-pointer transition-colors hover:brightness-95"
                          >
                            {content}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          sideOffset={4}
                          className="max-w-none overflow-visible border-none bg-transparent p-0 shadow-none"
                        >
                          <CalendarDayPopup
                        today={propertyToday}
                            date={day.date}
                            dayOfWeek={day.dayOfWeek}
                            currency={currency || 'GBP'}
                            rate={rateByDate?.[day.date]}
                            occupancy={{
                              booked: data.booked,
                              available: data.available,
                              occupancyPct: data.occupancyPct,
                              pickup7d: data.pickup7d,
                              pickup7dRooms: data.pickup7dRooms,
                            }}
                            propertyOccupancy={propertyOccupancyByDate?.[day.date]}
                            adr={adrData?.[day.date]?.adr}
                            lastYearAdr={adrData?.[day.date]?.lastYearAdr}
                          />
                        </TooltipContent>
                      </Tooltip>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  )
}

export default React.memo(OccupancyGrid)
